/**
 * MyGymRequestsPage Component
 *
 * Lists the current user's gym registration requests.
 * Shows status for each request and provides navigation to details or new registration.
 */

import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useGymRequests } from '@/hooks/useGymRequests';
import { GymRequestCard } from '@/components/gym-request/GymRequestCard';

/**
 * MyGymRequestsPage displays all gym requests submitted by the current user
 *
 * @example
 * ```tsx
 * // In router (wrapped in ProtectedRoute)
 * <Route path="/gym-requests" element={<ProtectedRoute><MyGymRequestsPage /></ProtectedRoute>} />
 * ```
 */
export const MyGymRequestsPage = () => {
  const { requests, loading, error, refetch } = useGymRequests();

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading your gym requests...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error Loading Requests</h2>
            <p style={errorTextStyle}>{error}</p>
            <button onClick={() => refetch()} style={retryButtonStyle}>
              Try Again
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <div style={headerMainStyle}>
            <div>
              <h1 style={titleStyle}>My Gym Requests</h1>
              <p style={subtitleStyle}>
                {requests.length === 0
                  ? 'You have not submitted any gym registration requests yet.'
                  : `You have ${requests.length} gym registration request${requests.length !== 1 ? 's' : ''}.`}
              </p>
            </div>
            <Link to="/gyms/register" style={registerButtonStyle}>
              Register New Gym
            </Link>
          </div>
        </header>

        {/* Empty State */}
        {requests.length === 0 && (
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>
              <span style={emptyIconTextStyle}>+</span>
            </div>
            <h2 style={emptyTitleStyle}>No gym requests yet</h2>
            <p style={emptyDescriptionStyle}>
              Want to add your climbing gym to Flash? Register your gym and start tracking routes
              for your community.
            </p>
            <Link to="/gyms/register" style={emptyActionStyle}>
              Register Your Gym
            </Link>
          </div>
        )}

        {/* Request List */}
        {requests.length > 0 && (
          <div style={listContainerStyle}>
            <div style={requestsGridStyle}>
              {requests.map((request) => (
                <GymRequestCard key={request.id} request={request} />
              ))}
            </div>
          </div>
        )}

        {/* Status Legend */}
        {requests.length > 0 && (
          <div style={legendStyle}>
            <h3 style={legendTitleStyle}>Status Guide</h3>
            <div style={legendItemsStyle}>
              <div style={legendItemStyle}>
                <span style={{ ...legendBadgeStyle, backgroundColor: '#fef3c7', color: '#92400e' }}>
                  Pending
                </span>
                <span style={legendDescStyle}>Awaiting admin review</span>
              </div>
              <div style={legendItemStyle}>
                <span style={{ ...legendBadgeStyle, backgroundColor: '#d1fae5', color: '#065f46' }}>
                  Approved
                </span>
                <span style={legendDescStyle}>Gym has been created</span>
              </div>
              <div style={legendItemStyle}>
                <span style={{ ...legendBadgeStyle, backgroundColor: '#ffedd5', color: '#9a3412' }}>
                  Changes Requested
                </span>
                <span style={legendDescStyle}>Updates needed - click to edit</span>
              </div>
              <div style={legendItemStyle}>
                <span style={{ ...legendBadgeStyle, backgroundColor: '#fee2e2', color: '#991b1b' }}>
                  Rejected
                </span>
                <span style={legendDescStyle}>Request was not approved</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f9fafb',
};

const contentStyle: CSSProperties = {
  maxWidth: '800px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: '#6b7280',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  marginBottom: '16px',
};

const retryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const headerMainStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '16px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1f2937',
  margin: '0 0 4px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  margin: 0,
};

const registerButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '12px 20px',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: 500,
  borderRadius: '8px',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const emptyStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '60px 20px',
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
};

const emptyIconStyle: CSSProperties = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
};

const emptyIconTextStyle: CSSProperties = {
  fontSize: '2.5rem',
  color: '#3b82f6',
  fontWeight: 300,
};

const emptyTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1f2937',
  margin: '0 0 8px 0',
};

const emptyDescriptionStyle: CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
  maxWidth: '400px',
  lineHeight: 1.6,
  margin: '0 0 20px 0',
};

const emptyActionStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '14px 28px',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontSize: '1rem',
  fontWeight: 500,
  borderRadius: '8px',
  textDecoration: 'none',
};

const listContainerStyle: CSSProperties = {
  marginBottom: '24px',
};

const requestsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '16px',
};

const legendStyle: CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  padding: '20px',
};

const legendTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  margin: '0 0 16px 0',
};

const legendItemsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '12px',
};

const legendItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const legendBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '4px 10px',
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: '9999px',
  whiteSpace: 'nowrap',
};

const legendDescStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: '#6b7280',
};

export default MyGymRequestsPage;
